import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Sidebar from '../../components/ui/Sidebar';
import XPProgressHeader from '../../components/ui/XPProgressHeader';
import AIChatbot from '../../components/ui/AIChatbot';
import CultureValueCard from './components/CultureValueCard';
import RitualCard from './components/RitualCard';
import CultureTimeline from './components/CultureTimeline';
import TeamStoryCard from './components/TeamStoryCard';
import CultureQuizPreview from './components/CultureQuizPreview';
import UpcomingEventsCard from './components/UpcomingEventsCard';

const CultureAndRitualsHub = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('values');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const coreValues = [
  {
    icon: "Target",
    title: "Customer Obsession",
    description: "We start with the customer and work backwards. Every decision we make is filtered through the lens of customer impact and value creation.",
    color: "bg-primary/10"
  },
  {
    icon: "Lightbulb",
    title: "Innovation First",
    description: "We embrace bold ideas and calculated risks. Failure is a stepping stone to breakthrough solutions that transform industries.",
    color: "bg-accent/10"
  },
  {
    icon: "Users",
    title: "Team Collaboration",
    description: "We believe diverse perspectives create stronger outcomes. Open communication and mutual respect are the foundation of our success.",
    color: "bg-success/10"
  },
  {
    icon: "TrendingUp",
    title: "Continuous Learning",
    description: "We invest in growth at every level. Curiosity drives us forward, and we celebrate both expertise and the courage to ask questions.",
    color: "bg-warning/10"
  },
  {
    icon: "Shield",
    title: "Integrity & Trust",
    description: "We do what's right, even when it's difficult. Transparency and ethical behavior are non-negotiable pillars of our culture.",
    color: "bg-error/10"
  },
  {
    icon: "Zap",
    title: "Bias for Action",
    description: "We move fast and iterate. Speed matters, but we balance urgency with thoughtful execution and data-driven decisions.",
    color: "bg-primary/10"
  }];


  const teamRituals = [
  {
    image: "https://images.unsplash.com/photo-1542744095-fcf48d80b0fd",
    imageAlt: "Diverse team of professionals sitting around modern conference table with laptops and coffee cups during morning standup meeting in bright office with large windows",
    title: "Daily Standup",
    frequency: "Daily",
    description: "15-minute sync to align on priorities, blockers, and wins. Keeps everyone connected and moving forward together.",
    participants: "All team members"
  },
  {
    image: "https://images.unsplash.com/photo-1576267423048-15c0040fec78",
    imageAlt: "Group of colleagues celebrating success with raised hands and smiles in modern office space with exposed brick walls and natural lighting during team celebration",
    title: "Friday Wins Celebration",
    frequency: "Weekly",
    description: "End the week by sharing accomplishments, big and small. Recognition fuels motivation and builds team morale.",
    participants: "Company-wide"
  },
  {
    image: "https://images.unsplash.com/photo-1552664730-d307ca884978",
    imageAlt: "Team members engaged in collaborative brainstorming session with sticky notes on glass wall and whiteboard markers in creative workspace with colorful furniture",
    title: "Innovation Sprints",
    frequency: "Monthly",
    description: "Dedicated time to explore new ideas, experiment with technologies, and prototype solutions outside daily work.",
    participants: "Cross-functional teams"
  },
  {
    image: "https://images.unsplash.com/photo-1716703435453-a7733d600d68",
    imageAlt: "Professional team conducting quarterly retrospective meeting with facilitator at whiteboard showing timeline and action items in modern conference room with natural light",
    title: "Quarterly Retrospectives",
    frequency: "Quarterly",
    description: "Deep reflection on what worked, what didn't, and how we can improve. Continuous evolution is our competitive advantage.",
    participants: "Department-level"
  }];


  const culturalMilestones = [
  {
    icon: "Rocket",
    title: "Company Founded",
    date: "January 2020",
    description: "Started with a vision to transform employee onboarding through AI-powered experiences and gamification."
  },
  {
    icon: "Award",
    title: "First 100 Employees",
    date: "June 2021",
    description: "Celebrated our growth milestone with a company-wide retreat focused on culture and values alignment."
  },
  {
    icon: "Globe",
    title: "Global Expansion",
    date: "March 2022",
    description: "Opened offices in three continents, embracing cultural diversity while maintaining our core values."
  },
  {
    icon: "Heart",
    title: "Wellness Program Launch",
    date: "September 2023",
    description: "Introduced comprehensive mental health support, flexible work arrangements, and wellness initiatives."
  },
  {
    icon: "Trophy",
    title: "Best Workplace Award",
    date: "November 2024",
    description: "Recognized as a top employer for innovation, culture, and employee satisfaction by industry leaders."
  }];


  const teamStories = [
  {
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_10d60e496-1763295319842.png",
    avatarAlt: "Professional headshot of Asian woman with long black hair wearing navy blazer and white blouse smiling warmly at camera",
    name: "Sarah Chen",
    role: "Senior Product Manager",
    quote: "The culture here isn't just words on a wall—it's lived every day. When I proposed a risky feature pivot, the team rallied behind the data and trusted the process. That's real innovation culture.",
    date: "15 Nov 2024"
  },
  {
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1f383ff29-1763295330729.png",
    avatarAlt: "Professional headshot of Hispanic man with short black hair and beard wearing charcoal suit and light blue shirt with confident smile",
    name: "Marcus Rodriguez",
    role: "Engineering Lead",
    quote: "I've worked at five companies, and this is the first place where 'work-life balance' isn't just a recruiting pitch. My manager actively encourages me to disconnect and recharge.",
    date: "08 Nov 2024"
  },
  {
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_17faa8e7a-1763295754680.png",
    avatarAlt: "Professional headshot of Black woman with natural curly hair wearing burgundy blazer and gold necklace with warm professional smile",
    name: "Aisha Okonkwo",
    role: "Customer Success Director",
    quote: "The customer obsession value is real. Last month, we delayed a feature launch to ensure quality. Short-term pain, but our customers noticed and appreciated the commitment.",
    date: "22 Oct 2024"
  }];


  const upcomingEvents = [
  {
    month: "DEC",
    day: "15",
    title: "Holiday Team Building",
    description: "Annual celebration with games, food, and year-end reflections",
    time: "2:00 PM - 6:00 PM",
    location: "Main Office",
    type: "optional"
  },
  {
    month: "JAN",
    day: "10",
    title: "Q1 Kickoff Meeting",
    description: "Company-wide strategy session and goal setting for the new quarter",
    time: "9:00 AM - 12:00 PM",
    location: "Virtual & In-Person",
    type: "mandatory"
  },
  {
    month: "JAN",
    day: "25",
    title: "Lunch & Learn: AI Ethics",
    description: "Guest speaker on responsible AI development and implementation",
    time: "12:00 PM - 1:00 PM",
    location: "Conference Room A",
    type: "optional"
  },
  {
    month: "FEB",
    day: "05",
    title: "Diversity & Inclusion Workshop",
    description: "Interactive session on building inclusive teams and practices",
    time: "10:00 AM - 3:00 PM",
    location: "Training Center",
    type: "mandatory"
  }];


  const tabs = [
  { id: 'values', label: 'Core Values', icon: 'Heart' },
  { id: 'rituals', label: 'Team Rituals', icon: 'Calendar' },
  { id: 'stories', label: 'Team Stories', icon: 'MessageSquare' },
  { id: 'events', label: 'Events', icon: 'PartyPopper' }];


  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} />
      <XPProgressHeader currentXP={450} level="Newbie" totalXP={1000} recentGain={0} />
      <main className={`pt-20 transition-all duration-300 ${isSidebarCollapsed ? 'lg:pl-20' : 'lg:pl-60'}`}>
        <div className="p-6 lg:p-8 max-w-7xl mx-auto">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Users" size={24} color="var(--color-primary)" strokeWidth={2.5} />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Culture & Rituals Hub</h1>
                <p className="text-muted-foreground">Discover our values, traditions, and what makes us unique</p>
              </div>
            </div>

            <div className="bg-warning/10 border border-warning/30 rounded-lg p-4 flex items-start gap-3">
              <Icon name="Lock" size={20} color="var(--color-warning)" className="shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-warning mb-1">Preview Mode - Coming Soon</h3>
                <p className="text-sm text-muted-foreground">
                  This hub is currently in development. The content below shows what you'll experience in the full release, including interactive culture assessments, team connection tools, and personalized cultural onboarding paths.
                </p>
              </div>
            </div>
          </div>

          <div className="mb-6 border-b border-border">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {tabs?.map((tab) =>
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-all duration-200 whitespace-nowrap ${
                activeTab === tab?.id ?
                'bg-primary/10 text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'}`
                }>

                  <Icon name={tab?.icon} size={18} color="currentColor" />
                  <span className="font-medium">{tab?.label}</span>
                </button>
              )}
            </div>
          </div>

          {activeTab === 'values' &&
          <div className="space-y-8 animate-fade-in">
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">Our Core Values</h2>
                <p className="text-muted-foreground mb-6">
                  These principles guide every decision we make and define who we are as a company. They're not just aspirations—they're the foundation of how we work, collaborate, and grow together.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {coreValues?.map((value, index) =>
                <CultureValueCard key={index} {...value} />
                )}
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-xl font-bold text-foreground mb-4">Cultural Milestones</h3>
                <CultureTimeline events={culturalMilestones} />
              </div>

              <CultureQuizPreview />
            </div>
          }

          {activeTab === 'rituals' &&
          <div className="space-y-8 animate-fade-in">
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">Team Rituals & Traditions</h2>
                <p className="text-muted-foreground mb-6">
                  Our rituals create rhythm, connection, and shared experiences. They're the heartbeat of our culture—moments that bring us together and reinforce our values in action.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {teamRituals?.map((ritual, index) =>
                <RitualCard key={index} {...ritual} />
                )}
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="Info" size={20} color="var(--color-primary)" strokeWidth={2.5} />
                  </div>
                  <h3 className="text-lg font-bold text-foreground">Why Rituals Matter</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-muted/20 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="Users" size={18} color="var(--color-success)" />
                      <h4 className="font-semibold text-foreground">Build Connection</h4>
                    </div>
                    <p className="text-sm text-muted-foreground">Regular touchpoints strengthen relationships and create psychological safety</p>
                  </div>
                  <div className="bg-muted/20 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="Target" size={18} color="var(--color-accent)" />
                      <h4 className="font-semibold text-foreground">Align Goals</h4>
                    </div>
                    <p className="text-sm text-muted-foreground">Shared rituals ensure everyone moves in the same direction with clarity</p>
                  </div>
                  <div className="bg-muted/20 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="Sparkles" size={18} color="var(--color-warning)" />
                      <h4 className="font-semibold text-foreground">Celebrate Wins</h4>
                    </div>
                    <p className="text-sm text-muted-foreground">Recognition rituals boost morale and reinforce positive behaviors</p>
                  </div>
                  <div className="bg-muted/20 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="TrendingUp" size={18} color="var(--color-primary)" />
                      <h4 className="font-semibold text-foreground">Drive Improvement</h4>
                    </div>
                    <p className="text-sm text-muted-foreground">Retrospectives and feedback loops enable continuous evolution</p>
                  </div>
                </div>
              </div>
            </div>
          }

          {activeTab === 'stories' &&
          <div className="space-y-8 animate-fade-in">
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">Team Stories & Testimonials</h2>
                <p className="text-muted-foreground mb-6">
                  Hear directly from team members about their experiences living our culture. These authentic voices show how our values translate into daily work and meaningful impact.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {teamStories?.map((story, index) =>
                <TeamStoryCard key={index} {...story} />
                )}
                </div>
              </div>

              <div className="bg-gradient-to-br from-primary/5 to-accent/5 border border-primary/20 rounded-lg p-8 text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="MessageSquarePlus" size={32} color="var(--color-primary)" strokeWidth={2} />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">Share Your Story</h3>
                <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                  In the full release, you'll be able to contribute your own experiences and insights about our culture. Your voice matters and helps shape our collective story.
                </p>
                <Button variant="outline" disabled>
                  <Icon name="Lock" size={16} color="currentColor" />
                  Submit Story (Coming Soon)
                </Button>
              </div>
            </div>
          }

          {activeTab === 'events' &&
          <div className="space-y-8 animate-fade-in">
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">Cultural Events & Activities</h2>
                <p className="text-muted-foreground mb-6">
                  Stay connected with upcoming events that bring our culture to life. From team building to learning opportunities, these gatherings strengthen our community.
                </p>
              </div>

              <UpcomingEventsCard events={upcomingEvents} />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-success/10 rounded-full flex items-center justify-center">
                      <Icon name="PartyPopper" size={20} color="var(--color-success)" strokeWidth={2.5} />
                    </div>
                    <h3 className="text-lg font-bold text-foreground">Social Events</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Regular social gatherings help build relationships beyond work tasks. From happy hours to team outings, these moments create lasting bonds.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Icon name="Check" size={16} color="var(--color-success)" />
                      <span>Monthly team lunches</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Icon name="Check" size={16} color="var(--color-success)" />
                      <span>Quarterly off-site activities</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Icon name="Check" size={16} color="var(--color-success)" />
                      <span>Annual company retreat</span>
                    </div>
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center">
                      <Icon name="GraduationCap" size={20} color="var(--color-accent)" strokeWidth={2.5} />
                    </div>
                    <h3 className="text-lg font-bold text-foreground">Learning Events</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Continuous learning is core to our culture. We host regular sessions to expand knowledge, share expertise, and grow together.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Icon name="Check" size={16} color="var(--color-accent)" />
                      <span>Weekly lunch & learns</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Icon name="Check" size={16} color="var(--color-accent)" />
                      <span>Guest speaker series</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Icon name="Check" size={16} color="var(--color-accent)" />
                      <span>Skill-sharing workshops</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          }

          <div className="mt-8 flex items-center justify-between p-6 bg-card border border-border rounded-lg">
            <div className="flex items-center gap-3">
              <Icon name="Lightbulb" size={24} color="var(--color-primary)" />
              <div>
                <h3 className="font-semibold text-foreground">Have questions about our culture?</h3>
                <p className="text-sm text-muted-foreground">Chat with Bee, your AI assistant, for instant answers</p>
              </div>
            </div>
            <Button variant="default" onClick={() => navigate('/main-dashboard')}>
              Back to Dashboard
            </Button>
          </div>
        </div>
      </main>
      <AIChatbot />
    </div>);

};

export default CultureAndRitualsHub;